/**
 * coupon-user 用户端（见 components/Pages/Activity/Voucher/voucherapi.md §3）
 * 与 `api/activity.js` 一致：`getOrigin("www") + "/sapi/v4"`，再拼服务内路径 `/coupon/user/...`。
 * 若网关另有挂载，可设 `NEXT_PUBLIC_COUPON_USER_API_BASE`（不含末尾 `/`，且需已包含到 `/coupon` 之前的前缀，见该 env 下拼 path 方式）。
 */
import { Util, Request } from "@xt/xtbase";

const { getOrigin } = Util;
const { XtAxios4 } = Request;

const SAPI_V4 = () => `${getOrigin("www")}/sapi/v4`;

/** 与 Voucher / RewardHub 共用：租户 ID */
export function resolveCouponTenantId(appState) {
  const fromState = Number(appState?.tenant);
  if (Number.isFinite(fromState) && fromState > 0) return fromState;
  const fromEnv = Number(typeof process !== "undefined" && process.env.NEXT_PUBLIC_COUPON_TENANT_ID);
  if (Number.isFinite(fromEnv) && fromEnv > 0) return fromEnv;
  return 1;
}

/**
 * 列表请求完整路径：`{base}/coupon/user/coupons`（用户端 voucherapi.md §3.2）。
 * 默认 base = `{www}/sapi/v4`（与 activity 奖励中心同源前缀）。
 */
export function getCouponUserApiBase() {
  const fromEnv = typeof process !== "undefined" && process.env.NEXT_PUBLIC_COUPON_USER_API_BASE;
  if (fromEnv) return String(fromEnv).replace(/\/$/, "");
  return SAPI_V4();
}

/**
 * ISO 日期时间字符串（UTC+0），用于列表时间筛选 Query（voucherapi.md §1.3）。
 * 格式示例：`2026-07-02T09:22:18`（无时区后缀，固定为 UTC 时刻）。
 * @param {Date} d
 */
export function formatCouponIsoLocalDateTime(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

/**
 * POST /coupon/user/coupons/redeem — 兑换码领券（voucherapi.md §3.1）
 * @param {{ tenantId: number|string, userId: number|string, redeemKey: string, idempotencyKey?: string }} body
 */
export function post_user_coupons_redeem(body, config = {}) {
  return XtAxios4.post(`${getCouponUserApiBase()}/coupon/user/coupons/redeem`, body, {
    ...config,
    isResFull: true,
    autoErrorModal: false,
  });
}

/**
 * GET /coupon/user/coupons — 我的券分页（用户端 coupon-user，voucherapi.md §3.2）
 * @param {{
 *   tenantId: number|string,
 *   userId: number|string,
 *   pageNum?: number,
 *   pageSize?: number,
 *   status?: string,
 *   scene?: string,
 *   expireAtFrom?: string,
 *   expireAtTo?: string,
 *   grantedAtFrom?: string,
 *   grantedAtTo?: string,
 *   effectiveAtFrom?: string,
 *   effectiveAtTo?: string,
 *   sortBy?: string,
 *   sortOrder?: string,
 * }} params
 */
/**
 * GET /coupon/user/coupons/{id} — 用户券明细（voucherapi.md §3.3）
 * @param {string|number} id — user_coupon.id
 * @param {{ tenantId: number|string, userId: number|string }} params
 */
export function get_user_coupon_by_id(id, params, config = {}) {
  const couponId = id != null ? String(id) : "";
  return XtAxios4.get(`${getCouponUserApiBase()}/coupon/user/coupons/${encodeURIComponent(couponId)}`, {
    ...config,
    isResFull: true,
    autoErrorModal: false,
    params: {
      ...(params && typeof params === "object" ? params : {}),
      ...(config.params || {}),
    },
  });
}

export function get_user_coupons(params, config = {}) {
  return XtAxios4.get(`${getCouponUserApiBase()}/coupon/user/coupons`, {
    ...config,
    isResFull: true,
    autoErrorModal: false,
    params: {
      pageNum: 1,
      pageSize: 20,
      ...(params && typeof params === "object" ? params : {}),
      ...(config.params || {}),
    },
  });
}

/**
 * 仅取「我的券」总条数（分页接口的 totalCount），pageSize=1 减少流量。
 * 可传 §3.2 筛选字段（如 `status`、`expireAtFrom`）。
 */
export async function fetchUserCouponTotalCount(
  { tenantId, userId, ...listQuery } = {},
  axiosConfig = {}
) {
  const res = await get_user_coupons(
    { tenantId, userId, pageNum: 1, pageSize: 1, ...listQuery },
    axiosConfig
  );
  if (!res || res.rc !== 0) {
    return { ok: false, totalCount: null, mc: res?.mc };
  }
  const result = res.result || {};
  const tc = Number(result.totalCount);
  if (Number.isFinite(tc)) {
    return { ok: true, totalCount: tc };
  }
  const items = Array.isArray(result.items) ? result.items : [];
  return { ok: true, totalCount: items.length };
}

/**
 * 顺序拉取多页直至无更多或达到 maxPages（用于 C 端列表无服务端「全部」接口时）。
 * @param {object} opts — `tenantId`、`userId`、`pageSize`、`maxPages` 及 §3.2 其余筛选/排序字段（`status`、`expireAtFrom` 等）
 */
export async function fetchAllUserCoupons(
  { tenantId, userId, pageSize = 50, maxPages = 20, ...listQuery } = {},
  axiosConfig = {}
) {
  const all = [];
  let pageNum = 1;
  let totalCount = Infinity;

  while (pageNum <= maxPages && all.length < totalCount) {
    // eslint-disable-next-line no-await-in-loop
    const res = await get_user_coupons({ tenantId, userId, pageNum, pageSize, ...listQuery }, axiosConfig);
    if (!res || res.rc !== 0) {
      return { ok: false, mc: res?.mc, items: all };
    }
    const result = res.result || {};
    const items = Array.isArray(result.items) ? result.items : [];
    const tc = Number(result.totalCount);
    totalCount = Number.isFinite(tc) ? tc : all.length + items.length;
    all.push(...items);
    if (items.length === 0 || items.length < pageSize) break;
    pageNum += 1;
  }

  return { ok: true, items: all, totalCount };
}
